import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Ban,
  Bot,
  CalendarClock,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Rocket,
  ShieldCheck,
  Sparkles,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale, formatDate } from "@/i18n/format";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { MediaPreview, mediaItemFromPiece } from "@/components/media";
import { postFormatLabel } from "@/lib/post-format-label";
import { captionPreview, formatRelativeTime } from "@/lib/upcoming-posts";
import type { ActivityItem, ActivityTone } from "@/lib/activity-feed";
import type { ActivityEvent } from "@/hooks/use-activity";
import { AutopilotRunCard } from "./autopilot-run-card";

const TONE: Record<ActivityTone, { dot: string; icon?: LucideIcon }> = {
  success: { dot: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", icon: CheckCircle2 },
  failed: { dot: "bg-destructive/15 text-destructive", icon: XCircle },
  scheduled: { dot: "bg-sky-500/15 text-sky-600 dark:text-sky-400", icon: CalendarClock },
  cancelled: { dot: "bg-muted text-muted-foreground", icon: Ban },
  pending: { dot: "bg-amber-500/15 text-amber-600 dark:text-amber-400", icon: Loader2 },
  info: { dot: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
};

const TYPE_ICON: Record<ActivityEvent["type"], LucideIcon> = {
  job: Sparkles,
  post: CheckCircle2,
  boost: Rocket,
  reply: MessageCircle,
  review: ShieldCheck,
};

/** One entry on the activity timeline. */
export function ActivityTimelineItem({ item, isLast }: { item: ActivityItem; isLast: boolean }) {
  const { lead, tone } = item;
  const isAutopilot = lead.type === "job" && lead.title.startsWith("Autopilot run");
  // Posts show their outcome; everything else shows what kind of thing happened.
  const Icon =
    lead.type === "post" || tone === "failed" ? (TONE[tone].icon ?? TYPE_ICON[lead.type]) : TYPE_ICON[lead.type];

  return (
    <li className="relative flex gap-3 pb-3">
      {!isLast && <span aria-hidden="true" className="absolute left-4 top-9 bottom-0 w-px bg-border" />}
      <span
        aria-hidden="true"
        className={cn("relative z-[1] mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full", TONE[tone].dot)}
      >
        {lead.actor === "ai" && lead.type !== "post" ? (
          <Bot className="h-4 w-4" />
        ) : (
          <Icon className={cn("h-4 w-4", tone === "pending" && Icon === Loader2 && "animate-spin")} />
        )}
      </span>
      <div className="min-w-0 flex-1">
        {isAutopilot ? (
          <AutopilotRunCard event={lead} />
        ) : lead.post ? (
          <PostEntry item={item} />
        ) : (
          <GenericEntry event={lead} tone={tone} />
        )}
      </div>
    </li>
  );
}

function EntryTime({ at }: { at: string }) {
  const locale = useLocale();
  const date = new Date(at);
  return (
    <time
      dateTime={at}
      title={formatRelativeTime(date, locale)}
      className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground"
    >
      {formatDate(date, locale, { hour: "numeric", minute: "2-digit" })}
    </time>
  );
}

function ToneBadge({ tone }: { tone: ActivityTone }) {
  const { t } = useTranslation("activity");
  if (tone !== "failed" && tone !== "pending") return null;
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        tone === "failed" ? "bg-destructive/10 text-destructive" : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      )}
    >
      {t(`tone.${tone}`)}
    </span>
  );
}

function EntryCard({
  href,
  tone,
  children,
}: {
  href: string | null;
  tone: ActivityTone;
  children: React.ReactNode;
}) {
  const className = cn(
    "block rounded-xl border bg-card px-3.5 py-3 transition",
    href && "hover:border-primary/30 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    tone === "failed" && "border-destructive/40 bg-destructive/[0.03]",
  );
  return href ? (
    <Link to={href} className={className}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
}

function PostEntry({ item }: { item: ActivityItem }) {
  const { t } = useTranslation("activity");
  const { t: tCal } = useTranslation("calendar");
  const locale = useLocale();
  const { lead, tone } = item;
  const post = lead.post!;

  const title =
    post.status === "SCHEDULED"
      ? t("post.scheduled", {
          when: formatDate(new Date(post.scheduledFor), locale, {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
        })
      : t(`post.${post.status.toLowerCase()}`);
  const caption = captionPreview(post.caption);
  const media =
    post.mediaUrl || post.thumbnailUrl
      ? mediaItemFromPiece({
          id: lead.id,
          mediaUrls: post.mediaUrl ? [post.mediaUrl] : [],
          thumbnailUrl: post.thumbnailUrl,
        })
      : null;
  const errors = [...new Set(item.events.map((e) => e.post?.error).filter((e): e is string => !!e))];

  return (
    <EntryCard href={lead.href} tone={tone}>
      <div className="flex gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("text-sm font-semibold", tone === "failed" && "text-destructive")}>{title}</span>
            <ToneBadge tone={tone} />
            <span className="ml-auto">
              <EntryTime at={item.at} />
            </span>
          </div>

          <ul className="flex flex-wrap gap-1.5" aria-label={t("filter.posts")}>
            {item.events.map((e) => (
              <li
                key={e.id}
                className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-xs"
              >
                <PlatformIcon platform={e.post!.platform} size={12} />
                {postFormatLabel(e.post!.platform, e.post!.postType, tCal)}
              </li>
            ))}
          </ul>

          <p className={cn("truncate text-sm", caption ? "text-muted-foreground" : "italic text-muted-foreground/70")}>
            {caption || t("post.noCaption")}
          </p>

          {errors.length > 0 && (
            <div className="flex gap-2 rounded-lg bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <div className="space-y-0.5 break-words">
                {errors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            </div>
          )}

          <ActorLine actor={lead.actor} />
        </div>
        {media && <MediaPreview item={media} className="h-16 w-16 shrink-0 self-start" />}
      </div>
    </EntryCard>
  );
}

function GenericEntry({ event, tone }: { event: ActivityEvent; tone: ActivityTone }) {
  return (
    <EntryCard href={event.href} tone={tone}>
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn("text-sm font-medium", tone === "failed" && "text-destructive")}>{event.title}</span>
          <ToneBadge tone={tone} />
          <span className="ml-auto">
            <EntryTime at={event.at} />
          </span>
        </div>
        {event.detail && (
          <p className={cn("text-sm break-words", tone === "failed" ? "text-destructive/90" : "text-muted-foreground")}>
            {event.detail}
          </p>
        )}
        <ActorLine actor={event.actor} />
      </div>
    </EntryCard>
  );
}

function ActorLine({ actor }: { actor: ActivityEvent["actor"] }) {
  const { t } = useTranslation("activity");
  return <p className="text-[11px] text-muted-foreground">{t(`by.${actor}`)}</p>;
}
